import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { copyToClipboard } from '../utils/clipboard';

import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckIcon from '@mui/icons-material/Check';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SchoolIcon from '@mui/icons-material/School';

import { AppPagination } from '../components/ui/AppPagination';
import FilterSortPanel from '../components/ui/FilterSortPanel';

// Получаем базовый URL из env и добавляем префикс /api/v1
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ''}/api/v1`;

interface CourseStatistics {
  course_id: string;
  course_title: string;
  enrolled_count: number;
  total_attempts: number;
  successful_attempts: number;
  average_progress_percentage: number | null;
  average_score: number | null;
  passing_score: number | null;
}

interface CoursesApiResponse {
  items: CourseStatistics[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

const ProgramsPage: React.FC = () => {
  const navigate = useNavigate();

  const [courses, setCourses] = useState<CourseStatistics[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('course_title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
  });

  const token = localStorage.getItem('token');

  // Вычисляем, активен ли фильтр или поиск, отличный от дефолтного
  const isResetDisabled =
    !searchQuery &&
    sortBy === 'course_title' &&
    sortDir === 'asc' &&
    appliedFilters.search === '';

  const handleLogout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [token, navigate]);

  const fetchCourses = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string> = {
        page: page.toString(),
        size: pageSize.toString(),
        sort_by: sortBy,
        sort_dir: sortDir,
      };

      if (appliedFilters.search) params['search'] = appliedFilters.search;

      const queryParams = new URLSearchParams(params);

      const response = await fetch(
        `${API_BASE_URL}/sdo/courses/statistics/search?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': token
          },
        }
      );

      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(`Ошибка загрузки: ${response.statusText}`);
      }

      const data: CoursesApiResponse = await response.json();
      setCourses(data.items || []);
      setTotalItems(data.total || 0);

    } catch (err) {
      console.error('Failed to fetch courses:', err);
      setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
    // Убрали API_BASE_URL из массива зависимостей, так как теперь это константа вне компонента
  }, [page, pageSize, token, handleLogout, sortBy, sortDir, appliedFilters]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleSearchClick = () => {
    setPage(1);
    setAppliedFilters({
      search: searchQuery,
    });
  };

  const handleReset = () => {
    setSearchQuery('');
    setSortBy('course_title');
    setSortDir('asc');
    setPage(1);
    setAppliedFilters({
      search: '',
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearchClick();
  };

  const handleCopyId = async (id: string) => {
    const success = await copyToClipboard(id);
    if (success) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const sortOptions = [
    { value: 'course_title', label: 'Название программы' },
    { value: 'enrolled_count', label: 'Кол-во записанных' },
    { value: 'total_attempts', label: 'Всего попыток' },
    { value: 'successful_attempts', label: 'Успешных сдач' },
    { value: 'average_score', label: 'Средний балл' },
    { value: 'average_progress_percentage', label: 'Средний прогресс' },
  ];

  return (
    <div className="w-full">
      <div className="p-4 lg:p-8 space-y-8">

        {/* Заголовок */}
        <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
          <MenuBookIcon fontSize="large" className="text-blue-600" />
          Статистика программ
        </h1>

        {/* Панель фильтров и поиска */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50/50">
            <h3 className="text-lg font-medium text-gray-900">Список программ</h3>
          </div>

          {/* Исходное положение поиска (отдельная секция) */}
          <div className="px-6 py-4 border-b bg-gray-50 w-full">
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Поиск программы</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="text-gray-400" fontSize="small" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Введите название программы или UUID..."
                  className="w-full rounded-lg border border-gray-300 pl-10 pr-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Исходное положение фильтрации/сортировки */}
          <FilterSortPanel
            showSearch={false}
            searchTerm=""
            onSearchChange={() => {}}
            onSearchSubmit={() => {}}
            filters={[]}
            onFilterChange={() => {}}
            showDateRange={false}
            startDate={null}
            onStartDateChange={() => {}}
            endDate={null}
            onEndDateChange={() => {}}
            sortOptions={sortOptions}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortOrder={sortDir}
            onSortOrderChange={setSortDir}
          />

          {/* Кнопки управления в стиле ApplicationList */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={handleReset}
              disabled={loading || isResetDisabled}
              className={`px-6 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                isResetDisabled
                  ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed opacity-70'
                  : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-100 shadow-sm'
              }`}
            >
              Сбросить
            </button>
            <button
              onClick={handleSearchClick}
              disabled={loading}
              className={`px-6 py-2 rounded-lg text-white text-sm font-semibold transition-all duration-200 shadow-sm ${
                loading
                  ? 'bg-blue-600 opacity-50 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Найти
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center w-full py-20">
              <CircularProgress size={40} className="text-blue-600" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-red-600 bg-red-50">
              <p className="text-sm font-medium mb-2">{error}</p>
              <button onClick={fetchCourses} className="text-sm text-blue-700 hover:underline font-medium">
                Попробовать снова
              </button>
            </div>
          ) : courses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-gray-500">
              <p className="text-base">Данные отсутствуют</p>
            </div>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto min-h-[300px]">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[35%]">Программа / ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Записано</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Попыток</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Сдано</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Сред. прогресс</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Сред. балл / Порог</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {courses.map((course) => {
                      const isCopied = copiedId === course.course_id;
                      const avgProgress = course.average_progress_percentage ?? 0;
                      const avgScore = course.average_score ?? 0;
                      const passScore = course.passing_score ?? 0;

                      return (
                        <tr key={course.course_id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-col">
                              <a
                                href={`https://sdo-bpe.ru/courses/${course.course_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline mb-1 flex items-center gap-1 w-fit"
                              >
                                {course.course_title}
                                <OpenInNewIcon style={{ fontSize: 14 }} className="text-gray-400 opacity-0 group-hover:opacity-100" />
                              </a>
                              <div
                                className="flex items-center text-xs font-mono cursor-pointer w-fit select-none"
                                onClick={() => handleCopyId(course.course_id)}
                              >
                                {isCopied ? (
                                  <span className="text-green-600 font-semibold flex items-center animate-pulse">
                                    <CheckIcon style={{ fontSize: 14, marginRight: 4 }} /> Скопировано!
                                  </span>
                                ) : (
                                  <span className="text-gray-500 hover:text-gray-900 flex items-center">
                                    <ContentCopyIcon style={{ fontSize: 12, marginRight: 4 }} /> {course.course_id.slice(0, 8)}...
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 align-top">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {course.enrolled_count}
                            </span>
                          </td>
                          <td className="px-6 py-4 align-top text-sm text-gray-600">
                            {course.total_attempts}
                          </td>
                          <td className="px-6 py-4 align-top">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                course.successful_attempts > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                              {course.successful_attempts}
                            </span>
                          </td>
                          <td className="px-6 py-4 align-top">
                            <div className="flex items-center gap-2">
                                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                    <div
                                        className="bg-blue-500 h-1.5 rounded-full"
                                        style={{ width: `${avgProgress}%` }}
                                    ></div>
                                </div>
                                <span className="text-xs text-gray-600">{avgProgress.toFixed(1)}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 align-top">
                            <div className="flex flex-col">
                                <span className={`text-sm font-semibold ${avgScore >= passScore ? 'text-green-600' : 'text-orange-600'}`}>
                                    {avgScore.toFixed(2)}
                                </span>
                                <span className="text-[10px] text-gray-400 uppercase tracking-tighter">Порог: {passScore}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden divide-y divide-gray-200">
                {courses.map((course) => {
                   const avgProgress = course.average_progress_percentage ?? 0;
                   const avgScore = course.average_score ?? 0;
                   const passScore = course.passing_score ?? 0;

                   return (
                    <div key={course.course_id} className="p-4 space-y-4 bg-white">
                        <div className="flex flex-col">
                        <a href={`https://sdo-bpe.ru/courses/${course.course_id}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 mb-1">
                            {course.course_title}
                        </a>
                        <div className="text-xs font-mono text-gray-500" onClick={() => handleCopyId(course.course_id)}>
                            {course.course_id}
                        </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-3">
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500 uppercase mb-1">Записано / Сдано</span>
                            <span className="text-sm font-semibold text-gray-800">
                                {course.enrolled_count} / <span className="text-green-600">{course.successful_attempts}</span>
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500 uppercase mb-1">Всего попыток</span>
                            <span className="text-sm font-semibold text-gray-800">{course.total_attempts}</span>
                        </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col">
                                <span className="text-xs text-gray-500 uppercase mb-1 flex items-center gap-1">
                                    <AssessmentIcon style={{ fontSize: 12 }} /> Сред. балл
                                </span>
                                <span className="text-sm font-bold">
                                    {avgScore.toFixed(2)}
                                    <span className="text-gray-400 font-normal text-xs ml-1"> (из {passScore})</span>
                                </span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs text-gray-500 uppercase mb-1 flex items-center gap-1">
                                    <SchoolIcon style={{ fontSize: 12 }} /> Прогресс
                                </span>
                                <span className="text-sm font-bold text-blue-600">
                                    {avgProgress.toFixed(1)}%
                                </span>
                            </div>
                        </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!loading && !error && courses.length > 0 && (
            <div className="bg-white border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Показано <span className="font-medium">{(page - 1) * pageSize + 1}</span> - <span className="font-medium">{Math.min(page * pageSize, totalItems)}</span> из <span className="font-medium">{totalItems}</span>
              </div>
              <AppPagination page={page} totalItems={totalItems} pageSize={pageSize} onChange={setPage} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramsPage;